import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  InputLabel, Checkbox,
} from "@mui/material";
import { FilterRow, joins, operators } from "../FilterRow";
import add from "../../../../public/assets/icons/add.svg";
import theme from "../../../theme/Theme";
import { caseContext } from "../../organisms/workItemsTable";
import {
  codeTypeMap,
  dictionaryValueMap,
  fetchCodeSearch,
  getQaRules,
  getTokenPayload,
  getUnrestrictedDictionary
} from "../../../utils/api";
import {SearchProviders} from "../../../queries/QAruleData";
import {alert500, policiesContext, serviceLineMapper, warning500} from "../../../utils/common";

const defaultColumnOptions = [
  {
    description: "Coder for the qa rule",
    entityType: "qarule_collection",
    field: "owner",
    headerName: "Coder",
    id: "owner",
    sortable: true,
    width: 200
  },
  {
    description: "Billing Provider on the invoice",
    entityType: "qarule_collection",
    field: "billing_provider",
    headerName: "Billing Provider",
    id: "billing_provider",
    sortable: true,
    width: 200
  },
  {
    description: "Performing Provider on the invoice",
    entityType: "qarule_collection",
    field: "performing_provider",
    headerName: "Performing Provider",
    id: "performing_provider",
    sortable: true,
    width: 200
  },
  {
    description: "",
    entityType: "qarule_collection",
    field: "group",
    headerName: "Group",
    id: "group",
    sortable: true,
    width: 200
  },
  {
    description: "",
    entityType: "qarule_collection",
    field: "case_sample",
    headerName: "Case Sample",
    id: "case_sample",
    sortable: true,
    width: 200,
    selected: false
  },
  {
    description: "",
    entityType: "qarule_collection",
    field: "coder_sample",
    headerName: "Coder Sample",
    id: "coder_sample",
    sortable: true,
    width: 200,
    selected: false
  },
  {
    description: "The service line of the tenant",
    entityType: "qarule_collection",
    field: "service",
    headerName: "Service Line",
    id: "service",
    sortable: true,
    width: 200,
    selected: false
  },
  {
    description: "Ordering Provider on the invoice",
    entityType: "qarule_collection",
    field: "ordering_provider",
    headerName: "Ordering Provider",
    id: "ordering_provider",
    sortable: true,
    width: 200
  },
  {
    description: "Codes on the Clinical invoice and the Predicted invoice are the same",
    entityType: "qarule_collection",
    field: "are_clinical_and_predicted_cpt_codes_same",
    headerName: "Clinical and Predicted Codes are the same", 
    id: "are_clinical_and_predicted_cpt_codes_same",
    sortable: true,
    width: 200
  }
]

/* Grab all unrestricted dictionaries facilitated by Dictionary Lookup Helper except for the code search dictionaries */
const codesearch = ["ICD Codes", "CPT Codes", "Modifiers"];
let onSearchController = new AbortController();
let abortSignal = onSearchController.signal
export const hiddenFilters = ["qa_level", "automate_status"];

const checkAndAbortSearch = (abortSignal: AbortSignal) => {
  if (abortSignal.aborted) {
    onSearchController = new AbortController();
    abortSignal = onSearchController.signal;
  } else {
    onSearchController.abort();
    onSearchController = new AbortController();
    abortSignal = onSearchController.signal;
  }

  return abortSignal;
}

const searchFilterKeys = {
  "CPT Codes": "procedure_code",
  "Facility": "facility",
  "Note Specialty": "note_specialty",
  "Patient Class": "patient_class",
  "Patient Type": "patient_type",
  "Modifiers": "modifier",
  "ICD Codes": "diagnosis_code",
  "Billing Area": "billing_area"
};

//region Helper Functions
const mapCoders = (allCoders) => {
  return allCoders?.map(coder => coder.data.worker_id)
    .sort((coder1, coder2) => coder1.data?.first_name.localCompare(coder2.data?.first_name));
}

const mapGroups = (allGroups) => {
  return allGroups?.map(group => group.value)
    .sort((group1, group2) => group1.localeCompare(group2));
}

const searchProviders = async (searchTerm, client) => {
  const response = await client?.query({
    query: SearchProviders,
    variables: {
      name: Array.isArray(searchTerm) ? searchTerm[0] : searchTerm,
    },
    fetchPolicy: "no-cache",
  });
  if (response?.data?.SearchProviders?.data?.length === 0) return [];
  else {
    let data = response?.data?.SearchProviders?.data;
    data = data?.sort((a, b) => {
      if (a.prov_name < b.prov_name) return -1;
      if (a.prov_name > b.prov_name) return 1;
      return 0;
    });

    return data.map((row) => {
      return {
        value: row.prov_name,
        label: `${row.prov_name}: ${row.prov_id}`
      };
    });
  }
}
//endregion

const samplesSearch = async (client, directoryType, service, searchTerm, isGettingSelectedValue) => {
  // Abort any previous searches that are still running
  // abortSignal = checkAndAbortSearch(abortSignal);

  const filters = isGettingSelectedValue
      ? []
      : [{ key: "availability", operation: "eq", value: "AVAILABLE" }];

  const response = await getQaRules(client, directoryType, service, filters);
  if(response === undefined) return [];
  if (response.length === 0) return [];

  if(isGettingSelectedValue && !response.find(row => row.data.collection_id === searchTerm)) {
    response.push({ data: { name: "{{RULE DELETED}}", collection_id: searchTerm } });
  }

  const mappedResponse = response.map(row => {
    const label = row.data.name === "{{RULE DELETED}}"
        ? row.data.name
        : `${row.data.name}: ${row.data.availability ? "Active" : "Inactive"}`;
    return {
      value: row.data.collection_id,
      label: label
    };
  });

  return mappedResponse.sort((a, b) => {
    return a.label.localeCompare(b.label, 'en', {sensitivity: 'base'});
  });
}

export const CriteriaFilters = ({ client, setFilters, filters, ...props }: any) => {

  const setParentErrors = props?.setSnackBars;
  const allCoders = props?.allCoders;
  const allGroups = props?.allGroups;

  const isManual = filters?.filter(filter => filter.fieldId === "automate_status" && filter.value.includes("MANUAL")).length > 0;
  const isAutomated = filters?.filter(filter => filter.fieldId === "automate_status" && filter.value.includes("AUTOMATED")).length > 0;

  //region State
  const [warnings, setWarnings] = useState([]);
  const [errors, setErrors] = useState([]);
  const [processingMethod, setProcessingMethod] = useState([isManual, isAutomated]);

  const [loading, setLoading] = useState(true);
  const [uiFilters, setUiFilters] = useState(filters.filter(filter => !hiddenFilters.includes(filter.fieldId)).map((filter) => filter.filterIndex));

  const [clearFirstFilter, setClearFirstFilter] = useState(false);
  const [columnOptions, setColumnOptions] = useState(defaultColumnOptions);
  const { userContext } = React.useContext(policiesContext);
  
  const serviceLineOptions = userContext.activeContext.serviceLines.map(serviceLine =>  {
    return {    
      value: serviceLineMapper[serviceLine.name] ? serviceLineMapper[serviceLine.name]  : serviceLine.name,
      label: serviceLine.name}
  });

  const [optionsDict, setOptionsDict] = useState({
    "owner": mapCoders(allCoders),
    "billing_provider": {
        onSearch: async (searchTerm, isGettingSelectedValue) => {
            return await searchProviders(searchTerm, client);
        }
    },
    "performing_provider": {
      onSearch: async (searchTerm, isGettingSelectedValue) => {
        return await searchProviders(searchTerm, client);
      }
    },
    "ordering_provider": {
      onSearch: async (searchTerm, isGettingSelectedValue) => {
        return await searchProviders(searchTerm, client);
      }
    },
    "group": mapGroups(allGroups),
    "case_sample": {
      onSearch: async (searchTerm, isGettingSelectedValue) => {
        return await samplesSearch(client, "case_samples", serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name, searchTerm, isGettingSelectedValue);
      }
    },
    "coder_sample": {
      onSearch: async (searchTerm, isGettingSelectedValue) => {
        return await samplesSearch(client, "coder_samples", serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name, searchTerm, isGettingSelectedValue);
      }
    },
    "service": serviceLineOptions,
    "are_clinical_and_predicted_cpt_codes_same": ['true', 'false']
  });
  //endregion
  
  //region Helpers
  const onFilterModified = (filter: any, apply?: boolean) => {
    if (Array.isArray(filter?.value) && !filter?.operator?.includes("in")) {
      filter = {
        ...filter,
        value: filter.value[0]
      }
    }

    const l = [];
    let flag = false;
    filters?.map((obj) => {
      if (obj?.filterIndex !== filter?.filterIndex) {
        l.push(obj);
      } else {
        flag = true;
        l.push(filter);
      }
    });
    if (!flag) {
      l.push(filter);
    }
    // check to determine whether case_sample or coder_sample is selected
    // since they are mutually exclusive
    if(filter.fieldName === 'coder_sample'){
      columnOptions.map((item) => {
        if(item.field === 'case_sample') {
          item.selected = true;
        }
      });
    } else if(filter.fieldName === 'case_sample') {
      columnOptions.map((item) => {
        if(item.field === 'coder_sample') {
          item.selected = true;
        }
      });
    }

    setUiFilters(l.filter(f => !hiddenFilters.includes(f.fieldId)).map((f) => f.filterIndex));
    setFilters(l);
    toggleCaseCoderSampleView(l);
  };

  const onAddFilter = () => {
    let newFilters;
    let newUiFilterPosition;

    if (uiFilters?.length) {
      newUiFilterPosition = uiFilters[uiFilters?.length - 1] + 1;
      newFilters = [...uiFilters, newUiFilterPosition];
    } else {
      newUiFilterPosition = 0;
      newFilters = [0];
    }

    onFilterModified({
      filterIndex: newUiFilterPosition,
      fieldName: columnOptions?.[0]?.field,
      fieldId: columnOptions?.[0]?.id,
      operator: operators[0]?.value,
      value: null,
      join: uiFilters?.length !== 0
          ? joins[0]?.value === undefined ? "AND" : joins[0]?.value
          : undefined,
    });

    setUiFilters(newFilters);
  };

  const onFilterDelete = (uiFilter) => {
    if (uiFilter == 0) {
      if (uiFilters?.length === 1) {
        setUiFilters([]);
        setFilters([]);

        //clear off the selection when all filter rows are deleted.
        columnOptions.map((item) => {
          if(item.field === 'case_sample') {
            item.selected = false;
          }
          if(item.field === 'coder_sample') {
            item.selected = false;
          }
        });
      } else {
        let nextFilterIndex = uiFilters?.[1];
        const updatedUiFilters = uiFilters.slice(1);
        updatedUiFilters[0] = 0;
        setUiFilters(updatedUiFilters);
        const updatedFilters = [];
        filters?.map((filter) => {
          if (filter.filterIndex !== 0) {
            if (filter?.filterIndex !== nextFilterIndex) {
              updatedFilters.push(filter);
            } else {
              updatedFilters.push({
                ...filter,
                filterIndex: 0,
                join: undefined,
              });
            }
          }
        });
        setFilters(updatedFilters);
      }
    } else {
      setUiFilters(uiFilters?.filter((filter) => (filter != uiFilter)));
      const newFilters = filters?.filter(
        (filter) => filter.filterIndex != uiFilter
      );
      setFilters(newFilters);
      toggleCaseCoderSampleView(newFilters);
    }
  };

  //Function to toggle the case samples and coder sampler menu selection.
  const toggleCaseCoderSampleView = (filters) => {
    let isCaseSamplePresent = false;
    let isCoderSamplePresent = false;

    filters.map((filter) => {
      if (filter.fieldName === 'coder_sample') {
        isCoderSamplePresent = true;
      } else if (filter.fieldName === 'case_sample') {
        isCaseSamplePresent = true;
      }
    });

    if (isCoderSamplePresent) {
      columnOptions.map((item) => {
        if (item.field === 'case_sample') {
          item.selected = true;
        }
      });
    } else if (isCaseSamplePresent) {
      columnOptions.map((item) => {
        if (item.field === 'coder_sample') {
          item.selected = true;
        }
      });
    } else {
      columnOptions.map((item) => {
        if (item.field === 'case_sample') {
          item.selected = false;
        }
        if (item.field === 'coder_sample') {
          item.selected = false;
        }
      });
    }
  }
  //endregion

  useEffect(() => {
    if (abortSignal.aborted) {
      onSearchController = new AbortController();
      abortSignal = onSearchController.signal;
    }

    /* Reach out to the unrestricted dictionary endpoint and grab all dictionary titles */
    const getAllDictionaryOptions = async () => {
      const token = getTokenPayload();
      const tenantId = token?.['tenantId'];

      const tenantDictionaries = await getUnrestrictedDictionary(tenantId, "Dictionary Lookup Helper", abortSignal);
      let tenantNames = [];
      tenantDictionaries.rowValues.forEach(dictionary => {
        // Special Case, Patient Class and Patient Type are the same dictionary but two separate filters
        if (dictionary.Name === "Patient Class") {
          tenantNames.push({
            name: "Patient Class",
            searchKey: "Patient Class"
          });
          tenantNames.push({
            name: "Patient Type",
            searchKey: "Patient Class",
            modifier: (value: string) => value.toUpperCase()
          });
          return;
        }

        // General Case
        tenantNames.push({
          name: dictionary.Name,
          searchKey: dictionary.Name
        });
      });

      // Code search is included in the dictionary list but is handled separately
      tenantNames = tenantNames.filter(tenantName => !codesearch.includes(tenantName.name));

      let mappedDictionaries = tenantNames.map((tenantName, index) => {
        return {
          description: "",
          entityType: "qarule_collection",
          field: searchFilterKeys[tenantName.name],
          headerName: tenantName.name,
          id: tenantName.name,
          sortable: true,
          width: 200
        };
      });

      return {
        mappedDictionaries,
        tenantNames
      }
    }

    const setupOptionsDict = async (opts) => {
      // Setup the options dictionary, this will be used to populate the values dropdowns in the filter row
      return {
        ...optionsDict,
        ...opts.tenantNames.reduce((acc, tenantName, index) => {
          acc[tenantName.name] = {
            onSearch: async () => {
              // Abort any previous searches that are still running
              // abortSignal = checkAndAbortSearch(abortSignal);

              const token = getTokenPayload();
              const tenantId = token?.['tenantId'];
              const response = await getUnrestrictedDictionary(tenantId, tenantName.searchKey, abortSignal);
              const formattedResults = {
                rowValues: response.rowValues.map(row => {
                  if (tenantName.name === "Billing Area") {
                    return {
                      [dictionaryValueMap[tenantName.name]]:  row[dictionaryValueMap[tenantName.name] ?? tenantName.name],
                      id: row['Billing Area ID'] ?? row['Billing Area']
                    }
                  }
                  return {
                    [dictionaryValueMap[tenantName.name]]: tenantName.modifier
                        ? tenantName.modifier(row[dictionaryValueMap[tenantName.name]])
                        : row[dictionaryValueMap[tenantName.name] ?? tenantName.name]
                  }
                })
              };

              if (formattedResults.rowValues.length === 0) return [];
              const sortedValues = formattedResults.rowValues.sort((a, b) => {
                if (a[dictionaryValueMap[tenantName.name]] < b[dictionaryValueMap[tenantName.name]])
                  return -1;
                else if (a[dictionaryValueMap[tenantName.name]] > b[dictionaryValueMap[tenantName.name]])
                  return 1;
                else
                  return 0;
              })
              if (tenantName.name === "Billing Area") {
                const dedupedValues = sortedValues.filter((value, index, self) => 
                  self.findIndex(v => v[dictionaryValueMap[tenantName.name]] === value[dictionaryValueMap[tenantName.name]]) === index
                );
                return dedupedValues.map(value => {
                  return {
                    value: value.id,
                    label: value[dictionaryValueMap[tenantName.name]]
                  };
                });
              } else {
                  const dedupedValues = sortedValues.map(row => row[dictionaryValueMap[tenantName.name]]).filter((value, index, self) => self.indexOf(value) === index);
                  return dedupedValues.map(value => {
                    return {
                      value: value,
                      label: value
                    };
                  });
                }
            }
          };
          return acc;
        }, {}),
        ...codesearch.reduce((acc, name, index) => {
          acc[name] = {
            onSearch: async (searchTerm) => {
              // Abort any previous searches that are still running
              // abortSignal = checkAndAbortSearch(abortSignal);

              // Ensure some results are shown before any typing
              let searchToken = searchTerm;
              if (searchToken === "" && name !== "Modifiers") searchToken = "9";

              const token = getTokenPayload();
              const tenantId = token?.['tenantId'];
              const response = await fetchCodeSearch(tenantId, searchToken, codeTypeMap[name], abortSignal)
              if (response.content.length === 0) return [];
              return response.content.sort((a: any, b: any) => {
                if (a.code < b.code)
                  return -1;
                else if (a.code > b.code)
                  return 1;
                else
                  return 0;
              }).map(row => {
                return {
                  value: row.code,
                  label: `${row.code} - ${row.description}`
                };
              });
            }
          };
          return acc;
        }, {})
      };
    }

    const setupColumnOptions = async (opts) => {
      // Setup the column options, this will be used to populate the options dropdown in the filter row
      const finalOpts = [
        ...defaultColumnOptions,
        ...opts.mappedDictionaries,
        ...codesearch.map((name, index) => ({
          description: "",
          entityType: "qarule_collection",
          field: searchFilterKeys[name],
          headerName: name,
          id: name,
          sortable: true,
          width: 200
        })),
      ];

      // Only show specific filters for specific sample levels
      const filteredOpts = finalOpts.filter((opt) => {
        if (opt.headerName === "Group") {
          return props?.directoryType === "verification_samples" || props?.directoryType === "cqa_samples";
        }
        if(opt.field === "coder_sample" || opt.field === "case_sample") {
          return props?.directoryType === "verification_samples";
        }
        return true;
      });

      const sortedOpts = filteredOpts.sort((a, b) => {
        if (a.headerName < b.headerName)
          return -1;
        else if (a.headerName > b.headerName)
          return 1;
        else
          return 0;
      });
      return sortedOpts;
    }

    const init = async () => {
      setLoading(true);
      try {
        const opts = await getAllDictionaryOptions();
        const optionsDict = await setupOptionsDict(opts);
        const columnOpts = await setupColumnOptions(opts);

        setColumnOptions(columnOpts);
        setOptionsDict(optionsDict);
      } catch (error) {
        console.error(`Error getting criteria filters: ${error}`);
      }
      setLoading(false);
    }

    init();
  }, []);

  useEffect(() => {
    // Processing Method Checkbox Logic
    const processingMethodFilter = filters?.filter(filter => filter.fieldId === "automate_status");
    const value = [];
    if(processingMethod[0]) value.push("MANUAL");
    if(processingMethod[1]) value.push("AUTOMATED");

    if (processingMethodFilter.length === 0) {
      setFilters([
        ...filters,
        {
          fieldName: "automate_status",
          fieldId: "automate_status",
          filterIndex: -1,
          join: undefined,
          operator: "in",
          value: value
        }
      ]);
    } else {
      const updatedFilters = filters.map(filter => {
        if (filter.fieldId === "automate_status") {
          return {
            ...filter,
            value: value
          }
        }
        return filter;
      });
      setFilters(updatedFilters);
    }

    // Processing Method Message Logic
    /*
    * If both manual and automated are selected and an owner filter is present
    * add a warning message to warnings
    *
    * If only automated is selected and an owner filter is present
    * add an error message to errors and parentErrors
    * */
    setWarnings([]);
    setErrors([]);
    setParentErrors([]);

    if (processingMethod[0] && processingMethod[1]) {
      if (filters?.filter(filter => filter.fieldId === "owner").length > 0) {
        const message = "You've added a collection for automated cases on a sample that includes criteria for coders. " +
            "Since automated cases aren't worked by coders, additional samples will not appear in the worklist.";
        setWarnings([message]);
      }
    } else if (processingMethod[1]) {
      if (filters?.filter(filter => filter.fieldId === "owner").length > 0) {
        const message = "You've added a collection for automated cases on a sample that includes criteria for coders. " +
            "This will result in zero results. Please remove the coders or add manual processing to this definition.";
        setErrors([message]);
        setParentErrors([message]);
      }
    }
  }, [processingMethod, uiFilters]);

  return (
    <Box minHeight={"50px"} width={"100%"}>
      <caseContext.Provider value={{
        directoryType: "QARULE_COLLECTION",
        columnOptions: columnOptions,
        detailGridOptions: { columnDefs: columnOptions },
        optionsDict: optionsDict,
        setOptionsDict: setOptionsDict
      }}
      >
        <InputLabel sx={{ display: "flex" }} htmlFor="criteria-filters">
        </InputLabel>

        <Box
          sx={{
            border: `1px solid ${theme.palette.artisticStroke200.main}`,
            borderRadius: "4px",
            padding: "10px",
            marginBottom: "20px",
            minWidth: "70%",
          }}
          id="criteria-filters"
        >
          <Typography style={{
            fontSize: "14px",
            marginTop: "-19px",
            marginLeft: "10px",
            backgroundColor: "white",
            width: "fit-content",
            padding: "0px 5px 0px 5px",
          }}
          >
            Criteria
          </Typography>

          {loading ?
              (
                  <Box sx={{
                    width: "100%",
                    height: "150px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}>
                    <CircularProgress color={"primary"} size={20}/>
                  </Box>
              ) : (
                  <Box sx={{padding: "10px 25px", overflow: "auto"}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: "12px"}}>
                      <Box>
                        <Typography>
                          Processing method:
                        </Typography>
                      </Box>

              <Box sx={{display: "flex", alignItems: "center"}}>
                <Checkbox style={{padding: "0 4px 0 0"}}
                            checked={processingMethod[0]}
                            onChange={() => setProcessingMethod([!processingMethod[0], processingMethod[1]])}
                />
                <Typography>
                  Manual
                </Typography>
              </Box>

              <Box sx={{display: "flex", alignItems: "center"}}>
                <Checkbox style={{padding: "0 4px 0 0"}}
                            checked={processingMethod[1]}
                            onChange={() => setProcessingMethod([processingMethod[0], !processingMethod[1]])}
                />
                <Typography>
                  Automated
                </Typography>
              </Box>
            </Box>

                    {uiFilters?.length ? (
                        <>
                          {uiFilters.map((uiFilter, index) => {
                            return (
                                <Box
                                    key={uiFilter}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                      margin: "10px 30px 10px 0px",
                                    }}
                                >
                                  <div key={`${allCoders.length}`}>
                                  {/* {optionsDict.billing_area && optionsDict.billing_area.length === 0 ? (
                                        <Typography>No options available for Billing Area</Typography>
                                    ) : ( */}

                                    <FilterRow
                                        client={client}
                                        filterIndex={uiFilter}
                                        uiIndex={index}
                                        resetAll={
                                          uiFilter == 0
                                              ? clearFirstFilter
                                              : undefined
                                        }
                                        setResetAll={setClearFirstFilter}
                                        onFilterModified={onFilterModified}
                                        currentFilter={
                                          filters?.filter(
                                              (filter) =>
                                                  filter.filterIndex === uiFilter
                                          )?.[0]
                                        }
                                        onFilterDelete={onFilterDelete}
                                        allCoders={allCoders}
                                        allGroups={allGroups}
                                    />
                                    {/* )} */}
                                  </div>
                                </Box>
                            );
                          })}
                        </>
                    ) : (
                        <Typography sx={{color: "#abafb0", marginTop: "5px"}}>
                          No filter conditions are applied
                        </Typography>
                    )}

                    <Box sx={{display: "flex", flexDirection: "row", gap: "5px"}}>
                      {errors?.map((error, index) => {
                        return (
                            <Box key={index} sx={{
                              background: alert500,
                              color: "white",
                              fontSize: "14px",
                              borderRadius: "4px",
                              padding: "6px"
                            }}>
                              {error}
                            </Box>
                        );
                      })}
                      {warnings?.map((warning, index) => {
                        return (
                            <Box key={index} sx={{
                              background: warning500,
                              color: "white",
                              fontSize: "14px",
                              borderRadius: "4px",
                              padding: "6px"
                            }}>
                              {warning}
                            </Box>
                        );
                      })}
                    </Box>

                    <Box sx={{
                      display: "flex",
                      gap: "5px",
                      flexDirection: "row",
                      ml: "-16px",
                    }}
                    >
                      <Button
                          onClick={onAddFilter}
                          disableRipple={true}
                          sx={{textTransform: "none"}}
                      >
                        <IconButton disableRipple={true}>
                          <img src={add} alt="add" style={{height: "14px"}}/>
                        </IconButton>
                        Add Rule
                      </Button>
                    </Box>
                  </Box>
              )
          }
        </Box>
      </caseContext.Provider>
    </Box>
  )
}
